/**
 * M1b — manual per-car start list for the selected stage.
 *
 * A plain roster × start-time table: one row per enrollment (the
 * enrollment IS the device↔car pairing — driver/vehicle labels ride on
 * it), one editable HH:MM field for THIS stage. Sparse by design: only
 * cars that get a time have an entry; a car without one falls back to
 * the stage's generic start time (Schedule card), or has no schedule
 * trigger at all when the stage has none. Gates/manual still apply
 * either way.
 *
 * Own Save button (like CircuitMapEditor) rather than riding the
 * page-level Save: the start list is a different editing rhythm —
 * you fill a column, not a form — and its PUT is behavioral every
 * time (any entry change bumps geofence_revision and re-emits
 * per-device geofence_set payloads; the backend resolves each car's
 * override into that device's stages[].start_utc).
 *
 * Armed enrollments are the rows that matter (their devices receive
 * payloads) and sort first; pending/disarmed/rejected rows render
 * dimmed with a state chip so the operator sees why a time typed
 * there won't reach a phone yet.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ListOrdered, Save } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  EnrollmentRow,
  EventRow,
  FleetApiError,
  StartListEntry,
  listEnrollments,
  updateEvent,
} from '@/services/fleet';

import { TIME_RE, formatError, normalizeHHMM } from './stageModel';

interface StartListCardProps {
  event: EventRow;
  stageIndex: number;
  /** Parent refresh — the PUT response carries the bumped revision. */
  onSaved: () => Promise<void> | void;
}

const STATE_ORDER: Record<string, number> = {
  armed: 0,
  pending: 1,
  disarmed: 2,
  rejected: 3,
};

/** This stage's entries, keyed by enrollment_id. */
function stageEntries(event: EventRow, stageIndex: number): Map<number, string> {
  const out = new Map<number, string>();
  for (const e of event.start_list_json?.entries ?? []) {
    if (e.stage_index === stageIndex && e.start_time) {
      out.set(e.enrollment_id, e.start_time);
    }
  }
  return out;
}

/** This stage's persisted per-entry dates, keyed by enrollment_id.
 *  Dates aren't editable in this UI (API-authored, multi-day events) —
 *  they only round-trip so a save doesn't strip them off the column. */
function stageDates(event: EventRow, stageIndex: number): Map<number, string> {
  const out = new Map<number, string>();
  for (const e of event.start_list_json?.entries ?? []) {
    if (e.stage_index === stageIndex && e.date) {
      out.set(e.enrollment_id, e.date);
    }
  }
  return out;
}

export const StartListCard: React.FC<StartListCardProps> = ({
  event,
  stageIndex,
  onSaved,
}) => {
  const [rows, setRows] = useState<EnrollmentRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // enrollment_id → the input's current text ('' = no entry).
  const [times, setTimes] = useState<Map<number, string>>(new Map());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // Roster fetch — on mount + event switch. The enrollment list is
  // lifecycle-slow; no poll needed while the operator types times.
  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setLoadError(null);
    listEnrollments(event.id)
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(formatError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [event.id]);

  const persisted = useMemo(
    () => stageEntries(event, stageIndex),
    [event, stageIndex],
  );
  const dirty = useMemo(() => {
    if (!rows) return false;
    const ids = new Set([...persisted.keys(), ...times.keys()]);
    for (const id of ids) {
      if ((persisted.get(id) ?? '') !== (times.get(id) ?? '')) return true;
    }
    return false;
  }, [rows, persisted, times]);

  // Rehydrate the column when the stage selection or the persisted
  // blob changes — same anti-clobber posture as the page form: keyed
  // on id/stage/revision, not on every poll-refreshed object identity.
  // The revision bumps on ANY behavioral edit (gates, schedule, another
  // operator's start list…), so a revision-driven refresh must not
  // clobber unsaved typed times: skip the reset while the operator has
  // edits. `dirty` here is this render's value — the typed `times`
  // compared against the freshly-arrived blob's entries — which is
  // exactly the "does the operator still have unsaved edits relative
  // to what just came in?" question. After our OWN save the form is
  // snapped to what we sent, so dirty is false against the refreshed
  // blob and rehydrate proceeds. Event/stage switches always reset.
  const hydratedKeyRef = useRef('');
  useEffect(() => {
    const key = `${event.id}:${stageIndex}`;
    const switched = hydratedKeyRef.current !== key;
    hydratedKeyRef.current = key;
    if (!switched && dirty) return;
    setTimes(stageEntries(event, stageIndex));
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id, stageIndex, event.geofence_revision]);

  const sorted = useMemo(
    () =>
      (rows ?? [])
        .slice()
        .sort(
          (a, b) =>
            (STATE_ORDER[a.state] ?? 9) - (STATE_ORDER[b.state] ?? 9) ||
            a.id - b.id,
        ),
    [rows],
  );

  const setTime = (enrollmentId: number, value: string) => {
    const next = new Map(times);
    if (value.trim() === '') next.delete(enrollmentId);
    else next.set(enrollmentId, value);
    setTimes(next);
  };

  const handleSave = async () => {
    for (const [id, value] of times) {
      if (!TIME_RE.test(value.trim())) {
        const row = sorted.find((r) => r.id === id);
        setSaveError(
          `Start time for ${row?.driver_name || row?.device_id || `enrollment ${id}`} ` +
            'must be HH:MM (24h), e.g. 10:38.',
        );
        return;
      }
    }
    setSaving(true);
    setSaveError(null);
    try {
      // Rebuild the blob: other stages' entries pass through verbatim;
      // this stage's column is replaced wholesale by the form state.
      const others = (event.start_list_json?.entries ?? []).filter(
        (e) => e.stage_index !== stageIndex,
      );
      // Persisted per-entry dates round-trip untouched (not editable
      // here — see stageDates); times normalize to two-digit hours
      // ('9:31' → '09:31') because the backend pattern requires them.
      const dates = stageDates(event, stageIndex);
      const mine: StartListEntry[] = [...times.entries()].map(
        ([enrollment_id, start_time]) => {
          const date = dates.get(enrollment_id);
          return {
            enrollment_id,
            stage_index: stageIndex,
            start_time: normalizeHHMM(start_time),
            ...(date ? { date } : {}),
          };
        },
      );
      await updateEvent(event.id, {
        start_list_json: { version: 1, entries: [...others, ...mine] },
        // Lost-update guard: the blob above was rebuilt from this
        // client's event prop. If another operator's save bumped the
        // revision since, the backend 409s instead of letting this
        // save silently wipe theirs.
        start_list_expected_revision: event.geofence_revision,
      });
      // Snap the form to exactly what we sent (normalization may have
      // rewritten '9:31' → '09:31') so it matches the refreshed
      // persisted baseline — dirty goes false and rehydrate proceeds.
      setTimes(new Map(mine.map((e) => [e.enrollment_id, e.start_time])));
      await onSaved();
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      if (err instanceof FleetApiError && err.status === 409) {
        // Keep the typed values (times untouched) — the operator
        // compares against the refreshed list and saves again.
        setSaveError(
          'Start list changed by another operator — reload / wait for the list to refresh, then re-apply your times.',
        );
      } else {
        setSaveError(formatError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4">
      <CardContent className="flex flex-col gap-3 p-0">
        <div className="flex flex-wrap items-center gap-2">
          <ListOrdered className="size-4 text-accent" />
          <span className="text-sm font-semibold">Start list</span>
          <span className="text-xs text-fg-secondary">
            per-car start times for this stage · {event.timezone || 'UTC'}
          </span>
          <span className="flex-1" />
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !dirty || !rows}
            className="gap-1"
          >
            <Save className="size-3.5" />
            {saving ? 'Saving…' : 'Save start list'}
          </Button>
          {savedFlash && <span className="text-xs text-status-online">Saved.</span>}
        </div>

        {saveError && (
          <div className="rounded border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
            {saveError}
          </div>
        )}

        {loadError ? (
          <p className="text-sm text-status-error">{loadError}</p>
        ) : rows == null ? (
          <div className="flex items-center gap-2 py-2 text-sm text-fg-secondary">
            <Spinner /> Loading roster…
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            No cars in the roster yet — devices appear here after they join the
            event (Roster tab).
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-fg-secondary">
                  <th className="py-1.5 pr-3 font-medium">Driver</th>
                  <th className="py-1.5 pr-3 font-medium">Vehicle · device</th>
                  <th className="py-1.5 pr-3 font-medium">State</th>
                  <th className="py-1.5 font-medium">Start time</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => {
                  const armed = row.state === 'armed';
                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        'border-b border-border/50 last:border-0',
                        !armed && 'opacity-55',
                      )}
                    >
                      <td className="py-1.5 pr-3 font-medium">
                        {row.driver_name || '—'}
                      </td>
                      <td className="py-1.5 pr-3">
                        <span>{row.vehicle || '—'}</span>{' '}
                        <span className="font-mono text-xs text-fg-secondary">
                          {row.device_id}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3">
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                            armed
                              ? 'bg-status-online/15 text-status-online'
                              : 'bg-fg-secondary/10 text-fg-secondary',
                          )}
                        >
                          {row.state}
                        </span>
                      </td>
                      <td className="py-1.5">
                        <Input
                          value={times.get(row.id) ?? ''}
                          onChange={(e) => setTime(row.id, e.target.value)}
                          placeholder="HH:MM"
                          inputMode="numeric"
                          className="h-8 w-24 font-mono"
                          aria-label={`Start time for ${row.driver_name || row.device_id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-fg-secondary">
          A car&rsquo;s time here beats the stage&rsquo;s generic start time; cars
          without one fall back to it. Saving pushes the new schedule to armed
          phones (revision bump). Only <span className="font-semibold">armed</span>{' '}
          rows reach a device.
        </p>
      </CardContent>
    </Card>
  );
};

export default StartListCard;
